import { inject } from '@angular/core';
import { TokenService } from '@core/services/token/token.service';
import { NonprofitService } from '../../nonprofit/nonprofit.service';
import { ResolveFn } from '@angular/router';
import { NonprofitAPI } from '@core/models/npo.model';

/**
 * Resolves the active nonprofit for the application.
 * This resolver checks if the user is logged in and retrieves the active nonprofit. If there is no active nonprofit set, it defaults to the first nonprofit from the token info or returns a placeholder nonprofit.
 * @returns
 */
export const activeNpoResolver: ResolveFn<NonprofitAPI> = async () => {
  const tokenService = inject(TokenService);
  const nonprofitService = inject(NonprofitService);

  const loggedIn = tokenService.getIsLoggedIn();
  const tokenInfo = tokenService.tokenInfo;

  if (!loggedIn) {
    return null;
  }

  // set the active nonprofit based on previous state
  const activeNpoId = nonprofitService.activeNpoId;
  // If activeNpoId is set and activeNpo is already loaded for that ID, return it directly
  if (activeNpoId && nonprofitService.activeNpo && nonprofitService.activeNpo.id === activeNpoId) {
    await nonprofitService.syncUserAffinity();

    return nonprofitService.activeNpo;
  }
  // Otherwise, if activeNpoId is set, load the nonprofit
  if (activeNpoId) {
    await nonprofitService.setActiveNonprofit(
      activeNpoId,
      true
    );

    return nonprofitService.activeNpo;
  }

  // set the active nonprofit based on token info
  if (tokenInfo?.NonprofitIdArr?.length > 0) {
    const firstNonprofitId = tokenInfo?.NonprofitIdArr[0];
    // Only call setActiveNonprofit if activeNpo is not already loaded for firstNonprofitId
    if (!nonprofitService.activeNpo || nonprofitService.activeNpo.id !== firstNonprofitId) {
      await nonprofitService.setActiveNonprofit(
        firstNonprofitId,
        true
      );
    }

    return nonprofitService.activeNpo;
  }

  // return placeholder
  return {
    name: 'Placeholder',
    displayName: 'placeholder',
    classificationInfo: {
      classificationOneId: 1,
      classificationTwoId: 2,
      classificationThreeId: 3,
      classificationTypeId: 1,
      classificationOneCode: 'I'
    },
    registrationId: '1',
    supportsSDGGive: false,
    supportsSDGVolunteer: true,
    displayAddress: null,
    displayEmail: null,
    displayUrl: null,
    displayNumber: null,
    displayPitch: null,
    remittanceInfo: {
      name: null,
      address: {
        address1: null,
        address2: null,
        city: null,
        stateProvRegCode: null,
        stateProvRegId: null,
        postalCode: null,
        countryCode: null
      }
    },
    nonprofitIconImageUrl: null,
    nonprofitLogoImageUrl: null,
    nonprofitHeaderImageUrl: null,
    sdGs: [{
      sdgId: 1,
      givePercent: 0.6,
      volunteerPercent: 0.4,
      startDate: '',
      targets: [{
        sdgTargetId: 1,
        target: 'Some target',
        givePercent: 0.6,
        volunteerPercent: 0.4,
        startDate: '',
        endDate: ''
      }]
    }, {
      sdgId: 2,
      givePercent: 0.5,
      volunteerPercent: 0.5,
      startDate: '',
      targets: []
    }],
    subsectionCode: '3',
    isActive: true
  } as NonprofitAPI;
};
